import { FormEvent, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Contact } from '../../types/contact';
import { Form, FormField, FormLabel, FormInput, FormSelect, FormCheckbox, Button } from '../ui';
import { assistantService } from '../../services';
import { sessionService } from '../../services';

interface ContactFormProps {
  contact: Contact | null;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

interface FormData {
  contact: string;
  name: string;
  is_custom_name: boolean;
  channel: string;
  type: string;
  key: string;
  is_group: boolean;
  personal_assistant_id: string;
  whatsapp_id: string;
  ia_respond_always: boolean;
  disable_chatbot: boolean;
  tags: string;
  disable_survey: boolean;
}

function ContactForm({ contact, onSubmit, isLoading, onClose }: ContactFormProps) {
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState<FormData>({
    contact: contact?.contact || '',
    name: contact?.name || '',
    is_custom_name: contact?.is_custom_name || false,
    channel: contact?.channel || 'whatsapp',
    type: contact?.type || 'other',
    key: contact?.key || '',
    is_group: contact?.is_group || false,
    personal_assistant_id: contact?.personal_assistant_id?.toString() || '',
    whatsapp_id: contact?.whatsapp_id?.toString() || '',
    ia_respond_always: contact?.ia_respond_always || false,
    disable_chatbot: contact?.disable_chatbot || false,
    tags: contact?.tags ? contact.tags.join(', ') : '',
    disable_survey: contact?.disable_survey || false,
  });

  useEffect(() => {
    if (contact) {
      setFormData({
        contact: contact.contact || '',
        name: contact.name || '',
        is_custom_name: contact.is_custom_name || false,
        channel: contact.channel || 'whatsapp',
        type: contact.type || 'other',
        key: contact.key || '',
        is_group: contact.is_group || false,
        personal_assistant_id: contact.personal_assistant_id?.toString() || '',
        whatsapp_id: contact.whatsapp_id?.toString() || '',
        ia_respond_always: contact.ia_respond_always || false,
        disable_chatbot: contact.disable_chatbot || false,
        tags: contact.tags ? contact.tags.join(', ') : '',
        disable_survey: contact.disable_survey || false,
      });
    }
  }, [contact]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const { data: assistants } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => assistantService.getAssistants(),
  });

  const { data: sessions } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => sessionService.getSessions(),
  });

  return (
    <Form onSubmit={onSubmit}>
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'general'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Geral
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Configurações
          </button>
        </nav>
      </div>

      <div className={activeTab === 'general' ? 'block' : 'hidden'}>
        <div className="space-y-4">
          <FormField>
            <FormLabel htmlFor="contact" required>
              Contato
            </FormLabel>
            <FormInput
              id="contact"
              name="contact"
              value={formData.contact}
              onChange={handleInputChange}
              required
              placeholder="Ex: 5511999999999 ou email@example.com"
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="name">Nome</FormLabel>
            <FormInput
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Nome do contato"
            />
          </FormField>

          <FormField>
            <div className="flex items-center">
              <FormCheckbox
                id="is_custom_name"
                name="is_custom_name"
                checked={formData.is_custom_name}
                onChange={handleInputChange}
              />
              <FormLabel htmlFor="is_custom_name" className="ml-2 mb-0">
                Nome personalizado
              </FormLabel>
            </div>
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField>
              <FormLabel htmlFor="channel" required>
                Canal
              </FormLabel>
              <FormSelect
                id="channel"
                name="channel"
                value={formData.channel}
                onChange={handleInputChange}
                required
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="sms">SMS</option>
                <option value="phone">Telefone</option>
                <option value="website">Website</option>
                <option value="telegram">Telegram</option>
                <option value="messenger">Messenger</option>
                <option value="api">API</option>
                <option value="none">Nenhum</option>
                <option value="other">Outro</option>
              </FormSelect>
            </FormField>

            <FormField>
              <FormLabel htmlFor="type" required>
                Tipo
              </FormLabel>
              <FormSelect
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
              >
                <option value="administrative">Administrativo</option>
                <option value="registration">Cadastro</option>
                <option value="commercial">Comercial</option>
                <option value="financial">Financeiro</option>
                <option value="legal_representative">Representante Legal</option>
                <option value="technical">Técnico</option>
                <option value="other">Outro</option>
              </FormSelect>
            </FormField>
          </div>

          <FormField>
            <FormLabel htmlFor="key">Chave</FormLabel>
            <FormInput
              id="key"
              name="key"
              value={formData.key}
              onChange={handleInputChange}
              placeholder="Chave de identificação"
              maxLength={255}
            />
          </FormField>

          <FormField>
            <div className="flex items-center">
              <FormCheckbox
                id="is_group"
                name="is_group"
                checked={formData.is_group}
                onChange={handleInputChange}
              />
              <FormLabel htmlFor="is_group" className="ml-2 mb-0">
                É um grupo
              </FormLabel>
            </div>
          </FormField>
        </div>
      </div>

      <div className={activeTab === 'settings' ? 'block' : 'hidden'}>
        <div className="space-y-4">
          <FormField>
            <FormLabel htmlFor="personal_assistant_id">Assistente Pessoal</FormLabel>
            <FormSelect
              id="personal_assistant_id"
              name="personal_assistant_id"
              value={formData.personal_assistant_id}
              onChange={handleInputChange}
            >
              <option value="">Nenhum (usar assistente padrão)</option>
              {assistants?.map(assistant => (
                <option key={assistant.id} value={assistant.id}>
                  {assistant.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel htmlFor="whatsapp_id">Sessão WhatsApp</FormLabel>
            <FormSelect
              id="whatsapp_id"
              name="whatsapp_id"
              value={formData.whatsapp_id}
              onChange={handleInputChange}
            >
              <option value="">Nenhuma (usar sessão padrão)</option>
              {sessions?.map(session => (
                <option key={session.id} value={session.id}>
                  {session.session_name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <FormCheckbox
                  id="ia_respond_always"
                  name="ia_respond_always"
                  checked={formData.ia_respond_always}
                  onChange={handleInputChange}
                />
              </div>
              <div className="ml-3 text-sm">
                <FormLabel htmlFor="ia_respond_always" className="font-medium">
                  IA Responde Sempre
                </FormLabel>
                <p className="text-gray-500 dark:text-gray-400">
                  A IA responderá automaticamente todas as mensagens deste contato
                </p>
              </div>
            </div>
          </FormField>

          <FormField>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <FormCheckbox
                  id="disable_chatbot"
                  name="disable_chatbot"
                  checked={formData.disable_chatbot}
                  onChange={handleInputChange}
                />
              </div>
              <div className="ml-3 text-sm">
                <FormLabel htmlFor="disable_chatbot" className="font-medium">
                  Desabilitar Chatbot
                </FormLabel>
                <p className="text-gray-500 dark:text-gray-400">
                  Desabilita o chatbot para este contato
                </p>
              </div>
            </div>
          </FormField>

          <FormField>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <FormCheckbox
                  id="disable_survey"
                  name="disable_survey"
                  checked={formData.disable_survey}
                  onChange={handleInputChange}
                />
              </div>
              <div className="ml-3 text-sm">
                <FormLabel htmlFor="disable_survey" className="font-medium">
                  Desabilitar Pesquisas
                </FormLabel>
                <p className="text-gray-500 dark:text-gray-400">
                  Este contato não receberá pesquisas de satisfação
                </p>
              </div>
            </div>
          </FormField>

          <FormField>
            <FormLabel htmlFor="tags">Tags</FormLabel>
            <FormInput
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="Ex: vip, cliente, parceiro (separadas por vírgula)"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Separe as tags por vírgula
            </p>
          </FormField>
        </div>
      </div>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {contact ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default ContactForm;
